import { useEffect, useMemo, useState } from "react";
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Button,
  Paper,
  TextInput,
  Table,
  Avatar,
  Badge,
  Menu,
  ActionIcon,
  Pagination,
  Modal,
  Checkbox,
  Grid,
  Skeleton,
  Select,
} from "@mantine/core";
import {
  IconSearch,
  IconPlus,
  IconDotsVertical,
  IconTrash,
  IconKey,
  IconShieldLock,
  IconRefresh,
  IconEdit,
  IconMailForward,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";
import { PaginatedResponse, AdminProfile } from "../../../lib/types";
import {
  AdminPermissionList,
  PermissionLabels,
  AdminPermissions,
} from "../../../lib/data";
import { InviteAdminModal } from "./InviteAdminModal";
import PermissionGuard from "../../../components/PermissionGuard";
import { useAdminContext } from "../../../context/AdminContext";

const Team = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editPermsModalOpen, setEditPermsModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminProfile | null>(null);
  const [selectedPerms, setSelectedPerms] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<AdminProfile | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [reinviteLoadingId, setReinviteLoadingId] = useState<string | null>(null);
  const { admin: currentUser } = useAdminContext();

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    document.title = "Team Management | Virel Admin";
  }, []);

  const endpoint = useMemo(
    () =>
      Endpoints.GetAdmins({
        page,
        limit,
        search: debouncedSearch,
        role: roleFilter || undefined,
      }),
    [page, limit, debouncedSearch, roleFilter],
  );

  const { data, isLoading, reloadData } = usePerformRequest<
    PaginatedResponse<AdminProfile[]>
  >({
    method: endpoint.method,
    route: endpoint.route,
  });

  const admins = data?.data || [];
  const totalRecords = data?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / limit);

  const openDeleteModal = (admin: AdminProfile) => {
    setAdminToDelete(admin);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!adminToDelete) return;
    setDeleteLoading(true);
    try {
      await PerformRequest({
        method: Endpoints.DeleteAdmin(adminToDelete.id).method,
        route: Endpoints.DeleteAdmin(adminToDelete.id).route,
      });
      setDeleteModalOpen(false);
      setAdminToDelete(null);
      reloadData();
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleReinvite = async (admin: AdminProfile) => {
    setReinviteLoadingId(admin.id);
    try {
      await PerformRequest({
        method: Endpoints.ReinviteAdmin(admin.id).method,
        route: Endpoints.ReinviteAdmin(admin.id).route,
      });
      reloadData();
    } finally {
      setReinviteLoadingId(null);
    }
  };

  const openPermissionsModal = (admin: AdminProfile) => {
    setSelectedAdmin(admin);
    setSelectedPerms(admin.permissions || []);
    setEditPermsModalOpen(true);
  };

  const handleUpdatePermissions = async () => {
    if (!selectedAdmin) return;
    setProcessing(true);

    try {
      // 1. Update Profile (Name, Email, Role)
      const updateEndpoint = Endpoints.UpdateAdmin(selectedAdmin.id);
      await PerformRequest({
        method: updateEndpoint.method,
        route: updateEndpoint.route,
        data: {
          firstName: selectedAdmin.firstName,
          lastName: selectedAdmin.lastName,
          email: selectedAdmin.email,
          role: selectedAdmin.role,
        },
      });

      // 2. Update Permissions (if role is admin)
      if (selectedAdmin.role === "admin") {
        const permEndpoint = Endpoints.UpdateAdminPermissions(selectedAdmin.id);
        await PerformRequest({
          method: permEndpoint.method,
          route: permEndpoint.route,
          data: { permissions: selectedPerms },
        });
      }

      setEditPermsModalOpen(false);
      reloadData();
    } catch (error) {
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  const togglePermission = (perm: string) => {
    setSelectedPerms((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm],
    );
  };

  return (
    <PermissionGuard
      permission={AdminPermissions.MANAGE_ADMINS}
      fallback={
        <Container>
          <Text>You do not have permission to view this page.</Text>
        </Container>
      }
    >
      <Container size="xl">
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={1}>Team Members</Title>
              <Text c="dimmed">Manage admins and their permissions</Text>
            </div>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => setInviteModalOpen(true)}
            >
              Invite Admin
            </Button>
          </Group>

          <Paper p="md" withBorder>
            <Group mb="md">
              <TextInput
                placeholder="Search admins..."
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ flex: 1 }}
              />
              <Select
                placeholder="Filter by role"
                data={[
                  { value: "super_admin", label: "Super Admin" },
                  { value: "admin", label: "Admin" },
                ]}
                value={roleFilter}
                onChange={setRoleFilter}
                clearable
                style={{ width: 160 }}
              />
            </Group>

            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Admin</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Permissions</Table.Th>
                  <Table.Th>Last Login</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>
                        <Skeleton height={20} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} />
                      </Table.Td>
                    </Table.Tr>
                  ))
                ) : admins.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5} style={{ textAlign: "center" }}>
                      No admins found
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  admins.map((admin) => (
                    <Table.Tr key={admin.id}>
                      <Table.Td>
                        <Group gap="sm">
                          <Avatar color="green" radius="xl">
                            {admin.firstName?.[0] || admin.email[0]}
                          </Avatar>
                          <div>
                            <Text size="sm" fw={500}>
                              {admin.firstName} {admin.lastName}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {admin.email}
                            </Text>
                          </div>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={
                            admin.role === "super_admin" ? "orange" : "green"
                          }
                        >
                          {admin.role}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {admin.status === "invited" ? (
                          <Badge color="yellow" variant="light">
                            Invited
                          </Badge>
                        ) : admin.status === "suspended" ? (
                          <Badge color="red" variant="light">
                            Suspended
                          </Badge>
                        ) : (
                          <Badge color="green" variant="light">
                            Active
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          {admin.role === "super_admin" ? (
                            <Badge variant="dot" color="green">
                              All Permissions
                            </Badge>
                          ) : (
                            <Text size="sm">
                              {admin.permissions?.length || 0} permissions
                            </Text>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {admin.lastLoginAt
                          ? new Date(admin.lastLoginAt).toLocaleDateString()
                          : "Never"}
                      </Table.Td>
                      <Table.Td>
                        <Menu shadow="md" width={200}>
                          <Menu.Target>
                            <ActionIcon variant="subtle">
                              <IconDotsVertical size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconEdit size={14} />}
                              onClick={() => openPermissionsModal(admin)}
                              disabled={
                                admin.role === "super_admin" &&
                                currentUser?.role !== "super_admin"
                              }
                            >
                              Edit Admin
                            </Menu.Item>
                            {admin.status === "invited" &&
                              !admin.lastLoginAt && (
                                <Menu.Item
                                  leftSection={<IconMailForward size={14} />}
                                  onClick={() => handleReinvite(admin)}
                                  disabled={reinviteLoadingId === admin.id}
                                >
                                  {reinviteLoadingId === admin.id
                                    ? "Re-inviting..."
                                    : "Re-invite Admin"}
                                </Menu.Item>
                              )}
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() => openDeleteModal(admin)}
                              disabled={
                                admin.role === "super_admin" &&
                                currentUser?.role !== "super_admin"
                              }
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>

            <Group justify="center" mt="md">
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconRefresh size={14} />}
                onClick={() => reloadData()}
              >
                Reload Team
              </Button>
            </Group>

            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                />
              </Group>
            )}
          </Paper>
        </Stack>

        <InviteAdminModal
          opened={inviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          onSuccess={reloadData}
        />

        {/* Delete Admin Confirmation Modal */}
        <Modal
          opened={deleteModalOpen}
          onClose={() => {
            if (deleteLoading) return;
            setDeleteModalOpen(false);
            setAdminToDelete(null);
          }}
          title="Delete Admin"
          centered
          radius="md"
        >
          <Stack gap="md">
            <Text size="sm">
              Are you sure you want to delete{" "}
              <Text component="span" fw={600}>
                {adminToDelete?.firstName || adminToDelete?.email}
              </Text>
              ? This action cannot be undone.
            </Text>
            <Text size="xs" c="dimmed">
              The admin will lose access to the dashboard and all associated
              permissions.
            </Text>
            <Group justify="flex-end" mt="md">
              <Button
                variant="light"
                onClick={() => {
                  if (deleteLoading) return;
                  setDeleteModalOpen(false);
                  setAdminToDelete(null);
                }}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleConfirmDelete}
                loading={deleteLoading}
              >
                Delete Admin
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={editPermsModalOpen}
          centered
          onClose={() => setEditPermsModalOpen(false)}
          title="Edit Admin"
        >
          <Stack>
            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="First Name"
                  value={selectedAdmin?.firstName || ""}
                  onChange={(e) =>
                    setSelectedAdmin((prev) =>
                      prev ? { ...prev, firstName: e.target.value } : null,
                    )
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label="Last Name"
                  value={selectedAdmin?.lastName || ""}
                  onChange={(e) =>
                    setSelectedAdmin((prev) =>
                      prev ? { ...prev, lastName: e.target.value } : null,
                    )
                  }
                />
              </Grid.Col>
            </Grid>

            <TextInput
              label="Email"
              value={selectedAdmin?.email || ""}
              onChange={(e) =>
                setSelectedAdmin((prev) =>
                  prev ? { ...prev, email: e.target.value } : null,
                )
              }
            />

            <Select
              label="Role"
              data={[
                { value: "admin", label: "Admin" },
                { value: "super_admin", label: "Super Admin" },
              ]}
              value={selectedAdmin?.role || "admin"}
              onChange={(val) =>
                setSelectedAdmin((prev) =>
                  prev ? { ...prev, role: val || "admin" } : null,
                )
              }
              allowDeselect={false}
              disabled={currentUser?.role !== "super_admin"}
            />

            {selectedAdmin?.role === "admin" && (
              <>
                <Text size="sm" fw={500} mt="md">
                  Permissions
                </Text>
                <Grid>
                  {AdminPermissionList.map((perm) => (
                    <Grid.Col span={12} key={perm}>
                      <Checkbox
                        label={PermissionLabels[perm]}
                        checked={selectedPerms.includes(perm)}
                        onChange={() => togglePermission(perm)}
                      />
                    </Grid.Col>
                  ))}
                </Grid>
              </>
            )}

            <Group justify="flex-end">
              <Button
                variant="light"
                onClick={() => setEditPermsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleUpdatePermissions} loading={processing}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </PermissionGuard>
  );
};

export default Team;
